import { OpenQuestionAnswerAreaContent } from '@lessonup/pin-renderer';
import { OpenQuestionTeacherPin } from '@lessonup/pins-shared';
import {
  borderRadius,
  Card,
  Chip,
  colorSets,
  IconCheck,
  IndicatorMark,
  List,
  SpaceBetween,
  spacing,
  styled,
  TextElement,
} from '@lessonup/ui-components';
import React from 'react';
import { useEditorTranslation } from '../../../../hooks/useEditorTranslation';

export interface OpenQuestionAnswerAreaContentProps {
  pin: OpenQuestionTeacherPin;
  isPinComponentActive?: boolean;
  showPlaceholderCards?: boolean;
}

const placeHolderCards: AnswerCardPlaceholderSize[] = [
  'small',
  'small',
  'small',
  'small',
  'large',
  'large',
  'large',
  'small',
  'medium',
  'medium',
  'small',
  'small',
  'small',
  'large',
  'medium',
];

export function OpenQuestionAnswerAreaContentEditor({
  pin,
  isPinComponentActive = false,
  showPlaceholderCards,
}: OpenQuestionAnswerAreaContentProps) {
  const { t } = useEditorTranslation();
  const columns = pin.settings.layout === 'FIFTY_FIFTY' ? 2 : 3;
  const correctAnswers = pin.teacherSettings.correctAnswers ?? [];
  const pinContainsCorrectAnswer = correctAnswers.length > 0;

  const answerCards = showPlaceholderCards
    ? placeHolderCards.map((size, answerIndex) => <AnswerCardPlaceholder key={`card-${answerIndex}`} size={size} />)
    : [];

  if (pinContainsCorrectAnswer) {
    answerCards[0] = (
      <Card cardTheme="neutral" resizing="fixed" showBoxShadow showBorder={false}>
        <StyledContent direction="y" spacing={spacing.size8} padding={spacing.size4}>
          <SpaceBetween direction="x" spacing={spacing.size4} justifyContent="flex-start" alignItems="center">
            <IndicatorMark indicatorTheme="positive" content={<IconCheck />} />
            <Chip text={t('answerAreaPinComponent.openQuestion.correctAnswerCard')} colorMode="positive" />
          </SpaceBetween>
          <StyledAnswerWrapper>
            {correctAnswers.length > 1 ? (
              <List>
                {correctAnswers.map((answer, index) => (
                  <StyledCorrectAnswer key={`correct-answer-${index}`} size="small" textStyle="bodyText" as="li">
                    {answer}
                  </StyledCorrectAnswer>
                ))}
              </List>
            ) : (
              <StyledCorrectAnswer size="small" textStyle="bodyText">
                {correctAnswers[0]}
              </StyledCorrectAnswer>
            )}
          </StyledAnswerWrapper>
        </StyledContent>
      </Card>
    );
  }

  return (
    <OpenQuestionAnswerAreaContent columns={columns} answerCards={answerCards} showScrollbar={isPinComponentActive} />
  );
}

type AnswerCardPlaceholderSize = 'small' | 'medium' | 'large';

interface AnswerCardPlaceholderProps {
  size: AnswerCardPlaceholderSize;
}

const AnswerCardPlaceholder: React.FC<AnswerCardPlaceholderProps> = ({ size }) => {
  return <StyledAnswerCardPlaceholder size={size} />;
};

const StyledAnswerCardPlaceholder = styled.div<AnswerCardPlaceholderProps>`
  background-color: #d9d9d9; /* There is no supported color in our color system for this, but this component will get a new styling eventually. */
  width: 100%;

  ${({ size }) => {
    switch (size) {
      case 'small':
        return `
          height: 56px;
          min-height: 56px;
        `;
      case 'medium':
        return `
          height: 80px;
          min-height: 80px;
        `;
      case 'large':
        return `
          height: 99px;
          min-height: 99px;
        `;
    }
  }}
`;

const StyledContent = styled(SpaceBetween)`
  word-break: break-word;
`;

const StyledAnswerWrapper = styled.div`
  border-radius: ${borderRadius.rounded4};
  padding: ${spacing.size0} ${spacing.size4};
  border: 2px dashed ${colorSets.active.stroke};
`;

const StyledCorrectAnswer = styled(TextElement)`
  margin-left: 1em;
`;
